import { useCallback, useEffect, useState } from 'react'
import { Dropdown, NavDropdown } from 'react-bootstrap'
import Image from 'react-bootstrap/Image'
import {
  IoGrid,
  IoNotifications,
  IoSearch,
  IoCart,
  IoChatbubbleEllipses
} from 'react-icons/io5'
import { MessageCircleQuestion } from 'lucide-react'
import { Link, useLocation } from 'react-router'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { UpdateTotalNoti, updateTotalUnreadMessages } from '@/redux/slices/socket'
import {
  toggleSupportChat,
  headerLauncherMounted,
  headerLauncherUnmounted,
} from '@/redux/slices/supportChat'
import { useGetSupportUnreadQuery } from '@/redux/api/conversationApi'
import { getSidebarMenuItems } from '@/component/common/layout/sidebar/sidebarMenuItems'
import { useSocket } from '@/redux/socketContext'
import { authController } from '@/utils/authController'
import { useGetProfileQuery, useGetVendorProfileQuery } from '@/redux/api/userApi';
import { SocketEvent } from '@/services/constants';
import { useUnreadCountSocketSync } from '@/hooks/useUnreadCountSocketSync';
import { useReceiptUploadSocketSync } from '@/hooks/useReceiptUploadSocketSync';
import { useVendorImportSocketSync } from '@/hooks/useVendorImportSocketSync';
import { useProfileSocketSync } from '@/hooks/useProfileSocketSync';
import { useSalesTaxEnabled } from '@/hooks/useSalesTaxEnabled';
import useIsAuthenticated from '@/hooks/useAuth';
import useRentalDates from '@/hooks/useRentalDates';
import { buildSearchUrl } from '@/hooks/useSearchFilters';
import ProfilePicImage from '@gearghost/media/react';
import arrowUpIcon from '@/component/images/arrow-up.svg';
import useCart from '@/hooks/useCart';
import { PATHS } from '@/navigation/paths';
import { createLogger } from '@/utils/logger';
import { badgeCount } from '@/utils/badgeCount';
import HomeAppStoreBadge from './HomeAppStoreBadge';
import HeaderBlogLink from './HeaderBlogLink';

const logger = createLogger('HeaderMenu');

const DASH = PATHS.dashboard

const HeaderMenu = ({ dashboard }: { dashboard?: boolean }) => {
  const { socket } = useSocket()
  const dispatch = useAppDispatch()
  const logout = () => authController.logout()

  // RTK Query: Get user and vendor profiles
  const { data: user } = useGetProfileQuery()
  const salesTaxEnabled = useSalesTaxEnabled()
  useGetVendorProfileQuery(undefined, {
    skip: !user?.vendor?.id
  })

  const isLoggedIn = useIsAuthenticated()

  const { notificationCount, totalUnreadMessages } = useAppSelector(state => state.socket)
  const { pathname } = useLocation()

  // Support opens in place (the panel is SupportChatWidget's, mounted once
  // app-wide); this icon took over from a bubble that floated over the
  // bottom of every page (Tyler, 2026-10-09), which stays only on pages
  // without a header. Its count is read-only on
  // the backend; useUnreadCountSocketSync below refreshes it live, and
  // reading the thread clears it.
  const supportChatOpen = useAppSelector(state => state.supportChat.open)
  const { data: supportUnread } = useGetSupportUnreadQuery(undefined, {
    skip: !isLoggedIn,
  })

  // While the icon is on the page, the widget draws no bubble of its own;
  // a page without a header keeps the bubble (supportChat slice).
  useEffect(() => {
    if (!isLoggedIn) return
    dispatch(headerLauncherMounted())
    return () => {
      dispatch(headerLauncherUnmounted())
    }
  }, [isLoggedIn, dispatch])

  // Cart hook for displaying cart count
  const { totalQuantity, isLoading: isCartLoading } = useCart()
  const { startDate, endDate } = useRentalDates()

  // Track previous quantity for bounce animation
  const [prevQuantity, setPrevQuantity] = useState(totalQuantity)
  const [shouldBounce, setShouldBounce] = useState(false)

  // Detect quantity increase during render (no effect needed for the comparison)
  if (!isCartLoading && totalQuantity !== prevQuantity) {
    setPrevQuantity(totalQuantity)
    if (totalQuantity > prevQuantity) {
      setShouldBounce(true)
    }
  }

  // Clear bounce animation after 700ms
  useEffect(() => {
    if (!shouldBounce) return
    const timer = setTimeout(() => setShouldBounce(false), 700)
    return () => clearTimeout(timer)
  }, [shouldBounce])

  // Initialize unread messages count from user profile
  useEffect(() => {
    if (user?.totalUnreadMessages != null) {
      dispatch(updateTotalUnreadMessages(user.totalUnreadMessages))
    }
  }, [user?.totalUnreadMessages, dispatch])

  // Real-time unread badge + conversation-cache sync (extracted hook —
  // validates the wrapped UNREAD_COUNT payload with the shared schema).
  useUnreadCountSocketSync(socket)
  // A vendor's receipt uploads, kept current on every page from the read job's pushes.
  useReceiptUploadSocketSync()
  // A vendor's imports through the vendor API, and the items and listings they make, the same way.
  useVendorImportSocketSync()
  // The person's profile, store and verifications, when something away from their pages changes them.
  useProfileSocketSync()

  const onConnected = useCallback(() => {
    logger.info('socket connected')
  }, [])
  const onNotificationCount = useCallback((message: { payload: number }) => {
    if (pathname !== '/user-notifications') {
      dispatch(UpdateTotalNoti(message.payload))
    }
  }, [pathname, dispatch])

  useEffect(() => {
    if (!socket) return
    socket.on(SocketEvent.CONNECTED_EVENT, onConnected)
    socket.on(SocketEvent.NOTI_COUNT_EVENT, onNotificationCount)
    return () => {
      socket.off(SocketEvent.CONNECTED_EVENT, onConnected)
      socket.off(SocketEvent.NOTI_COUNT_EVENT, onNotificationCount)
    }
  }, [socket, onConnected, onNotificationCount])

  // The toolbox grid IS the sidebar (Tyler, 2026-09-10): both render the
  // one menu config by role, so an item added there shows here too. The
  // grid used to keep its own hardcoded list and had drifted (Favorites
  // only here, Become A Vendor only in the sidebar).
  const menuItems = getSidebarMenuItems(user?.role, { salesTaxEnabled })

  return (
    <div className='headerBlock'>
      <HomeAppStoreBadge />
      <div className='navSearch'>
        <Link
          to={buildSearchUrl({
            view: 'map',
            startDate: startDate ?? undefined,
            endDate: endDate ?? undefined,
          })}
          replace
        >
          <IoSearch className={dashboard ? 'nav-dashboard-color' : undefined} />
        </Link>
        <HeaderBlogLink dashboard={dashboard} />
        {/* Search and the blog sit before the line, the account's own icons after it (Tyler, 2026-10-27). */}
        <span className='divider'></span>
        <Link to={PATHS.cart} className='cardPos'>
          <IoCart className={dashboard ? 'nav-dashboard-color' : undefined} />
          {!isCartLoading && totalQuantity > 0 && (
            <span className={`count${shouldBounce ? ' cart-bump' : ''}`}>
              {totalQuantity > 99 ? '99+' : totalQuantity}
            </span>
          )}
        </Link>
        {/* Anyone signed in, as the bubble before it was; outside the
            role gate below. */}
        {isLoggedIn && (
          <button
            type='button'
            className='cardPos navIconButton'
            onClick={() => dispatch(toggleSupportChat())}
            aria-label='Open support chat'
            aria-expanded={supportChatOpen}
            data-testid='header-support'
          >
            <MessageCircleQuestion
              size='1em'
              aria-hidden='true'
              className={dashboard ? 'nav-dashboard-color' : undefined}
            />
            {!supportChatOpen && supportUnread && supportUnread.unreadCount > 0 && (
              <span className='count' data-testid='header-support-count'>
                {badgeCount(supportUnread.unreadCount)}
              </span>
            )}
          </button>
        )}
        {isLoggedIn && (user?.role === 'vendor' || user?.role === 'renter') && (
          <>
            <Link to={DASH.messages} className='cardPos'>
              <IoChatbubbleEllipses
                className={dashboard ? 'nav-dashboard-color' : undefined}
              />
              {totalUnreadMessages > 0 && (
                <span className='count'>
                  {badgeCount(totalUnreadMessages)}
                </span>
              )}
            </Link>
            <Link to={DASH.notifications} className='cardPos'>
              <IoNotifications className={dashboard ? 'nav-dashboard-color' : undefined} />
              {notificationCount > 0 && (
                <span className='count'>
                  {badgeCount(notificationCount)}
                </span>
              )}
            </Link>
            <span className='divider'></span>
            <NavDropdown
              className='dropdowngrid'
              title={
                <div>
                  <IoGrid className={dashboard ? 'nav-dashboard-color' : undefined} />
                </div>
              }
              id='basic-nav-dropdown2'
              align='end'
            >
              <span className='trainglelenotch'>
                <Image src={arrowUpIcon} className='img-fluid' alt='' />
              </span>
              {menuItems.map((item) => (
                <NavDropdown.Item key={item.id} as={Link} to={item.link}>
                  <span className='gridicon'>{item.icon}</span>
                  <span className='gridtext'>{item.name}</span>
                </NavDropdown.Item>
              ))}
            </NavDropdown>
            <Dropdown className='userprofiledropdown' align='end'>
              <Dropdown.Toggle
                variant='null'
                id='dropdown-basic'
                className='navMedia border-0 p-0 pointer'
              >
                <ProfilePicImage s3Path={user?.profilePic} alt='' />
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item as={Link} to='#' onClick={() => logout()}>
                  Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            {/* <div
              className="navMedia pointer"
              onClick={() => navigate("/user-profile")}
            >
              <Image
                src={getImage(user?.s3Path, "user")}
                alt=""
              />
            </div> */}
          </>
        )}
      </div>

      {(!isLoggedIn ||
        (user?.role !== 'vendor' && user?.role !== 'renter')) && (
        <div className='btnBox'>
          {pathname !== PATHS.login && (
            // Link styled as a button — react-bootstrap's polymorphic
            // `as={Link}` prop doesn't typecheck in this version.
            <Link className='btn btn-login-header btn-sm' to={PATHS.login}>
              Login
            </Link>
          )}
        </div>
      )}
    </div>
  )
}

export default HeaderMenu
